import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge"; 
import {
  Camera,
  CheckCircle2,
  AlertCircle,
  Clock,
  Eye,
  RotateCcw,
  Trash2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { createThumbnail } from "@/lib/photoUtils";

import { Trans} from "@lingui/macro";

interface PhotoStep {
  id: string;
  title: string;
  description: string;
  instruction: string;
  example: string;
  required?: boolean;
  category?: 'frontal' | 'profile' | 'occlusal' | 'buccal';
}

interface ModernPhotoGridProps {
  steps: PhotoStep[];
  capturedPhotos: Record<string, string>;
  currentStepIndex: number;
  onStepSelect: (index: number) => void;
  onPhotoView: (stepId: string) => void;
  onPhotoRetake: (stepId: string) => void;
  onPhotoDelete?: (stepId: string) => void;
  showActions?: boolean;
  className?: string;
}

export function ModernPhotoGrid({
  steps,
  capturedPhotos,
  currentStepIndex,
  onStepSelect,
  onPhotoView,
  onPhotoRetake,
  onPhotoDelete,
  showActions = true,
  className
}: ModernPhotoGridProps) {
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [loadingThumbnails, setLoadingThumbnails] = useState<Set<string>>(new Set());

  const capturedKey = JSON.stringify(capturedPhotos);
  useEffect(() => {
    let photos: Record<string, string> = {};
    try {
      photos = JSON.parse(capturedKey) as Record<string, string>;
    } catch {
      photos = {};
    }
    setThumbnails((prev) => {
      const next = { ...prev };
      for (const id of Object.keys(prev)) {
        if (!photos[id]) delete next[id];
      }
      return next;
    });
    setLoadingThumbnails(new Set());
  }, [capturedKey]);

  // Generate thumbnail for photo
  const generateThumbnail = useCallback(async (stepId: string, imageData: string) => {
    if (thumbnails[stepId] || loadingThumbnails.has(stepId)) return;
    
    if (!imageData || typeof imageData !== 'string' || imageData.length === 0) {
      console.warn('Invalid image data for thumbnail generation:', stepId);
      return;
    }

    setLoadingThumbnails(prev => new Set(prev).add(stepId));
    
    try {
      const thumbnail = await createThumbnail(imageData, 200);
      setThumbnails(prev => ({ ...prev, [stepId]: thumbnail }));
    } catch (error) {
      console.error('Failed to generate thumbnail for', stepId, ':', error);
    } finally {
      setLoadingThumbnails(prev => {
        const newSet = new Set(prev);
        newSet.delete(stepId);
        return newSet;
      });
    }
  }, [thumbnails, loadingThumbnails]);

  // Get photo stats
  const getPhotoStats = useCallback((imageData: string) => {
    if (!imageData || typeof imageData !== 'string') {
      return {
        size: '0KB',
        timestamp: Date.now()
      };
    }
    
    // Only calculate size for base64 data URLs, not for HTTP/blob URLs
    let size = 0;
    if (imageData.startsWith('data:')) {
      const base64 = imageData.split(",")[1] ?? "";
      const padding = (base64.match(/=+$/)?.[0]?.length ?? 0);
      size = Math.max(0, (base64.length * 3) / 4 - padding);
    }
    
    return {
      size: size > 0 
        ? (size > 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)}MB` : `${(size / 1024).toFixed(0)}KB`)
        : 'Unknown',
      timestamp: Date.now() // This would come from actual capture time
    };
  }, []);

  // Get category color
  const getCategoryColor = () => {
    return 'bg-gray-50 text-gray-500 border-gray-200/60 font-medium';
  };

  // Get completion stats
  const completionStats = {
    total: steps.length,
    completed: Object.keys(capturedPhotos).length,
    required: steps.filter(s => s.required !== false).length,
    requiredCompleted: steps.filter(s => s.required !== false && capturedPhotos[s.id]).length
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Progress Summary */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
          <h3 className="font-bold text-gray-900 tracking-tight text-base">
            <Trans>Photo Progress</Trans>
          </h3>
          <Badge variant="outline" className="w-fit bg-gray-50 text-gray-700 border-gray-200 rounded-lg px-2.5 tabular-nums">
            {completionStats.completed}/{completionStats.total} <Trans>Complete</Trans>
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm sm:divide-x sm:divide-gray-100">
          <div className="text-center">
            <div className="text-xl font-bold text-gray-900">{completionStats.completed}</div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-0.5"><Trans>Captured</Trans></div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-600 tabular-nums">
              {completionStats.requiredCompleted}
              <span className="mx-0.5 font-semibold text-gray-400">/</span>
              {completionStats.required}
            </div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-0.5">
              <Trans>Required</Trans>
            </div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-gray-900">{completionStats.total - completionStats.completed}</div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-0.5"><Trans>Remaining</Trans></div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-[#166B93]">
              {Math.round((completionStats.completed / completionStats.total) * 100)}%
            </div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-0.5"><Trans>Progress</Trans></div>
          </div>
        </div>
      </div>

      {/* Enhanced Photo Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 w-full">
        {steps.map((step, index) => {
          const isCompleted = !!capturedPhotos[step.id];
          const isCurrent = index === currentStepIndex;
          const imageData = capturedPhotos[step.id];
          const stats = imageData && typeof imageData === 'string' && imageData.length > 0 ? getPhotoStats(imageData) : null;

          // Generate thumbnail if needed (only for data URLs, not for HTTP/blob URLs)
          if (imageData && typeof imageData === 'string' && imageData.length > 0 && 
              imageData.startsWith('data:') && !thumbnails[step.id] && !loadingThumbnails.has(step.id)) {
            generateThumbnail(step.id, imageData);
          }

          return (
            <div
              key={step.id}
              className={cn(
                "relative group cursor-pointer transition-all duration-200",
                "border rounded-xl overflow-hidden bg-white",
                "hover:shadow-md hover:border-gray-300",
                isCurrent && "border-[#166B93] shadow-md bg-[#166B93]/[0.06]",
                isCompleted ? "border-emerald-200 bg-emerald-50/30" : "border-gray-200"
              )}
              onClick={() => {
                if (isCompleted) {
                  onPhotoView(step.id);
                } else {
                  onStepSelect(index);
                }
              }}
            >
              {/* Step Number Badge */}
              <div className={cn(
                "absolute top-1.5 left-1.5 z-10 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-bold shadow-sm transition-colors",
                isCompleted ? "bg-emerald-500 text-white" : 
                isCurrent ? "bg-[#166B93] text-white" : "bg-gray-100 text-gray-500 border border-gray-200"
              )}>
                {isCompleted ? '✓' : index + 1}
              </div>

              {/* Category Badge */}
              {step.category && (
                <div className="absolute top-1.5 right-1.5 z-10">
                  <Badge 
                    variant="outline" 
                    className={cn("text-[8px] sm:text-[10px] px-1.5 py-0 h-4 sm:h-5 shadow-sm", getCategoryColor())}
                  >
                    {step.category}
                  </Badge>
                </div>
              )}

              {/* Photo Area */}
              <div className="aspect-[4/3] relative">
                {isCompleted ? (
                  <>
                    <img
                      key={`${step.id}-${String(imageData).slice(0, 120)}-${String(imageData).length}`}
                      src={thumbnails[step.id] || imageData}
                      alt={step.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.warn(
                          "[ModernPhotoGrid] Image failed (e.g. revoked blob URL):",
                          step.id,
                          String(imageData).slice(0, 80),
                        );
                        setThumbnails((prev) => {
                          const n = { ...prev };
                          delete n[step.id];
                          return n;
                        });
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    
                    {/* Success Overlay */}
                    <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-white/90 rounded-full p-3">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                    </div>

                    {/* Photo Stats Overlay */}
                    {stats && showActions && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex justify-between items-center">
                            <span>{stats.size}</span>
                          <Clock className="w-3 h-3" />
                        </div>
                      </div>
                    )}

                    {/* Action Buttons Overlay */}
                    {showActions && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/90 hover:bg-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPhotoView(step.id);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/90 hover:bg-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPhotoRetake(step.id);
                          }}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        {onPhotoDelete && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="bg-red-100/90 hover:bg-red-100 text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPhotoDelete(step.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <Camera className={cn(
                      "w-10 h-10 mb-2",
                      isCurrent ? "text-[#166B93]" : "text-gray-400"
                    )} />
                    {isCurrent && (
                      <div className="text-xs font-medium text-[#166B93] animate-pulse">
                        <Trans>Current</Trans>
                      </div>
                    )}
                    {step.required !== false && !isCompleted && (
                      <AlertCircle className="w-4 h-4 text-orange-500 mt-1" />
                    )}
                  </div>
                )}
              </div>

              {/* Step Info */}
              <div className="p-2 sm:p-3 flex flex-col items-center justify-center min-h-[4rem] sm:min-h-0">
                <h4 className="font-semibold text-[11px] sm:text-sm leading-tight text-center text-gray-800 mb-0.5 sm:mb-1 line-clamp-2">
                  {step.title}
                </h4>
                
                {/* Step Badges */}
                <div className="flex items-center justify-center gap-1 mt-1">
                  {step.required !== false && !isCompleted && (
                    <Badge variant="outline" className="text-[8px] sm:text-[10px] text-[#166B93]/80 border-[#166B93]/20 bg-[#166B93]/5 font-medium px-1 sm:px-1.5 py-0 h-4 tracking-tight leading-none uppercase">
                      <Trans>Required</Trans>
                    </Badge>
                  )}
                </div>
              </div>

              {/* Current Step Indicator (subtle left edge) */}
              {isCurrent && !isCompleted && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#166B93] rounded-l-xl" />
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      {showActions && completionStats.completed > 0 && (
        <div className="flex flex-wrap gap-2 justify-center pt-4 border-t">
          {completionStats.completed < completionStats.total && (
            <Button variant="outline" size="sm" className="text-orange-600 border-orange-200">
              <AlertCircle className="w-4 h-4 mr-2" />
              {completionStats.total - completionStats.completed} <Trans>Photos Needed</Trans>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
